import { FC } from 'react';

import SearchDebug from 'src/components/Debug/SearchDebug';
import { useSelector } from 'src/hooks/useSelector';
import type { ResumeCardData } from 'src/models/resumeCard';
import { SettingType } from 'src/models/search/resume/result';

import AreaAndRelocation from 'src/components/ResumeCard/Body/AreaAndRelocation';
import Assessments from 'src/components/ResumeCard/Body/Assessments';
import Certificates from 'src/components/ResumeCard/Body/Certificates';
import Citizenship from 'src/components/ResumeCard/Body/Citizenship';
import Contacts from 'src/components/ResumeCard/Body/Contacts';
import Education from 'src/components/ResumeCard/Body/Education';
import Experience, { getShouldShowExperience } from 'src/components/ResumeCard/Body/Experience';
import KeySkills from 'src/components/ResumeCard/Body/KeySkills';
import Language from 'src/components/ResumeCard/Body/Language';
import LastQuestion from 'src/components/ResumeCard/Body/LastQuestion';
import LinkedResumes from 'src/components/ResumeCard/Body/LinkedResumes';
import PinnedMessages from 'src/components/ResumeCard/Body/PinnedMessages';
import ProfessionalRoles from 'src/components/ResumeCard/Body/ProfessionalRoles';
import TotalExperience from 'src/components/ResumeCard/Body/TotalExperience';

interface Props {
    resume: ResumeCardData;
    isGifted: boolean;
    isNew: boolean;
    markAsRead?: () => void;
    isResponse: boolean;
    goal?: string;
    vacancyId: string;
    showContacts: boolean;
    showDeepLinks: boolean;
    showVisited: boolean;
    visited: boolean;
    chatId?: number;
    onContactsClick?: () => void;
}

const Body: FC<Props> = ({
    resume,
    isGifted,
    isNew,
    markAsRead,
    isResponse,
    goal,
    vacancyId,
    showContacts,
    showDeepLinks,
    showVisited,
    visited,
    chatId,
    onContactsClick,
}) => {
    const activeSettings = useSelector((state) => state.resumeSearchResult.settings.activeSettings);
    return (
        <div>
            {<LastQuestion topicId={resume.topicId} chatId={chatId} />}
            {activeSettings.includes(SettingType.ExperienceSum) && <TotalExperience resume={resume} />}
            {getShouldShowExperience(activeSettings) && (
                <Experience
                    resume={resume}
                    isGifted={isGifted}
                    isNew={isNew}
                    markAsRead={markAsRead}
                    isVisited={showVisited && visited}
                />
            )}
            <PinnedMessages chatId={chatId} />
            <KeySkills resume={resume} />
            {activeSettings.includes(SettingType.ProfessionalRole) && (
                <ProfessionalRoles professionalRoles={resume?.professionalRole} />
            )}
            {activeSettings.includes(SettingType.Citizenship) && <Citizenship resume={resume} />}
            {activeSettings.includes(SettingType.Area) && <AreaAndRelocation resume={resume} />}
            {activeSettings.includes(SettingType.Education) && <Education resume={resume} />}
            {activeSettings.includes(SettingType.Languages) && <Language resume={resume} />}
            {activeSettings.includes(SettingType.Certificates) && <Certificates resume={resume} />}
            <LinkedResumes resume={resume} />
            {isResponse && Number(resume.assessmentsCount) > 0 && resume.topicId && (
                <Assessments topicId={resume.topicId} />
            )}
            {showContacts && (
                <Contacts
                    resume={resume}
                    isNew={isNew}
                    markAsRead={markAsRead}
                    isResponse={isResponse}
                    goal={goal}
                    vacancyId={vacancyId}
                    showDeepLinks={showDeepLinks}
                    visited={visited}
                    onContactsClick={onContactsClick}
                />
            )}
            {resume.searchDebug && <SearchDebug {...resume.searchDebug} />}
        </div>
    );
};

export default Body;
